import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Avatar,
    Button,
    Divider,
    Empty,
    Popconfirm,
    Space,
    Typography,
    message,
    theme,
} from 'antd';
import { CommentOutlined, DeleteOutlined, SendOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pl';
import { productPackagingApi } from '../../../api/client';
import RichMentionEditor from '../../../components/common/RichMentionEditor';
import MentionDisplay from '../../../components/common/MentionDisplay';
import type { PackagingComment } from '../../bdo/types';

dayjs.extend(relativeTime);
dayjs.locale('pl');

const { Text } = Typography;

interface Props {
    productId: string;
    isMobile?: boolean;
}

export default function PackagingComments({ productId, isMobile }: Props) {
    const { token } = theme.useToken();
    const queryClient = useQueryClient();
    const [commentText, setCommentText] = useState('');

    const { data: comments = [], isLoading } = useQuery<PackagingComment[]>({
        queryKey: ['packaging-comments', productId],
        queryFn: () =>
            productPackagingApi.getComments(productId).then((res: any) => res.data),
        enabled: !!productId,
    });

    const createMutation = useMutation({
        mutationFn: (content: string) =>
            productPackagingApi.addComment(productId, { content }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['packaging-comments', productId] });
            setCommentText('');
            message.success('Komentarz dodany');
        },
        onError: () => message.error('Błąd dodawania komentarza'),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) =>
            productPackagingApi.deleteComment(productId, id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['packaging-comments', productId] });
            message.success('Komentarz usunięty');
        },
        onError: () => message.error('Błąd usuwania komentarza'),
    });

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Space>
                <CommentOutlined />
                <Text strong>Komentarze</Text>
                <Text type="secondary">({comments.length})</Text>
            </Space>

            <RichMentionEditor
                value={commentText}
                onChange={setCommentText}
                placeholder="Napisz komentarz... (@wzmianka)"
                minRows={2}
            />
            <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => createMutation.mutate(commentText)}
                loading={createMutation.isPending}
                disabled={!commentText.trim()}
                block
            >
                Wyślij
            </Button>

            <Divider style={{ margin: '8px 0' }} />

            {isLoading ? (
                <Text type="secondary">Ładowanie komentarzy...</Text>
            ) : comments.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Brak komentarzy"
                />
            ) : (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {comments.map((comment) => (
                        <div
                            key={comment.id}
                            style={{
                                display: 'flex',
                                gap: 10,
                                background: token.colorFillQuaternary,
                                padding: 10,
                                borderRadius: 8,
                            }}
                        >
                            <Avatar
                                size={isMobile ? 'small' : 'default'}
                                style={{ backgroundColor: token.colorPrimary, flexShrink: 0 }}
                            >
                                {comment.author?.firstName?.[0] || '?'}
                            </Avatar>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <div>
                                        <Text strong style={{ fontSize: 13 }}>
                                            {comment.author?.firstName} {comment.author?.lastName}
                                        </Text>
                                        <div
                                            style={{
                                                fontSize: 11,
                                                color: token.colorTextDescription,
                                            }}
                                        >
                                            {dayjs(comment.createdAt).fromNow()}
                                        </div>
                                    </div>
                                    <Popconfirm
                                        title="Usunąć komentarz?"
                                        okText="Tak"
                                        cancelText="Nie"
                                        onConfirm={() => deleteMutation.mutate(comment.id)}
                                    >
                                        <Button
                                            type="text"
                                            danger
                                            size="small"
                                            icon={<DeleteOutlined />}
                                        />
                                    </Popconfirm>
                                </div>
                                <div style={{ fontSize: 13, lineHeight: 1.5, marginTop: 4 }}>
                                    <MentionDisplay content={comment.content} />
                                </div>
                            </div>
                        </div>
                    ))}
                </Space>
            )}
        </Space>
    );
}
